// The shopping board's shortcut palette: common foods and household things,
// grouped the way a store is laid out. Tapping one writes it on the board and
// adds it to the list. Glyphs are in glyphs.tsx.

import { itemKey } from "../../../shared/kitchen/validate";
import type { FoodGlyphName } from "./glyphs";

export interface Shortcut {
  name: string;
  glyph: FoodGlyphName;
  /** Other ways a list says the same thing (matched for icons on list items). */
  also?: string[];
}

export interface Aisle {
  id: string;
  name: string;
  /** For the narrow aisle rail, when the name is long. */
  short?: string;
  glyph: FoodGlyphName;
  items: Shortcut[];
}

const s = (name: string, glyph: FoodGlyphName, ...also: string[]): Shortcut => (also.length ? { name, glyph, also } : { name, glyph });

export const AISLES: Aisle[] = [
  {
    id: "produce",
    name: "Produce",
    glyph: "apple",
    items: [
      s("Apples", "apple"),
      s("Bananas", "banana"),
      s("Oranges", "orange", "clementines", "mandarins"),
      s("Lemons", "lemon", "limes"),
      s("Grapes", "grapes"),
      s("Strawberries", "strawberry", "berries", "blueberries", "raspberries"),
      s("Avocados", "avocado"),
      s("Tomatoes", "tomato"),
      s("Lettuce", "lettuce", "salad", "spinach"),
      s("Carrots", "carrot"),
      s("Potatoes", "potato"),
      s("Onions", "onion"),
      s("Garlic", "garlic"),
      s("Broccoli", "broccoli"),
      s("Peppers", "pepper", "bell peppers"),
      s("Cucumber", "cucumber", "zucchini"),
      s("Mushrooms", "mushroom"),
      s("Corn", "corn"),
    ],
  },
  {
    id: "dairy",
    name: "Dairy and eggs",
    short: "Dairy",
    glyph: "milk",
    items: [
      s("Milk", "milk"),
      s("Eggs", "egg"),
      s("Butter", "butter"),
      s("Cheese", "cheese", "cheddar", "cheddar cheese", "mozzarella", "parmesan"),
      s("Yogurt", "yogurt", "yoghurt"),
      s("Cream", "milk", "heavy cream", "half and half"),
      s("Sour cream", "tub"),
      s("Cream cheese", "tub"),
    ],
  },
  {
    id: "meat",
    name: "Meat and fish",
    short: "Meat & fish",
    glyph: "drumstick",
    items: [
      s("Chicken", "drumstick", "chicken breasts", "chicken thighs"),
      s("Ground beef", "steak", "mince", "hamburger"),
      s("Steak", "steak"),
      s("Bacon", "bacon"),
      s("Sausages", "sausage", "hot dogs"),
      s("Salmon", "fish", "fish", "tuna"),
      s("Shrimp", "shrimp", "prawns"),
      s("Deli meat", "deli", "ham", "turkey"),
    ],
  },
  {
    id: "bakery",
    name: "Bakery",
    glyph: "bread",
    items: [
      s("Bread", "bread"),
      s("Bagels", "bagel"),
      s("Tortillas", "tortilla", "wraps"),
      s("Buns", "bun", "rolls", "hamburger buns"),
      s("Croissants", "croissant"),
      s("Muffins", "muffin"),
      s("Cake", "cake"),
    ],
  },
  {
    id: "pantry",
    name: "Pantry",
    glyph: "jar",
    items: [
      s("Rice", "rice"),
      s("Pasta", "pasta", "spaghetti", "noodles"),
      s("Flour", "sack"),
      s("Sugar", "sack"),
      s("Cereal", "cereal"),
      s("Oatmeal", "cereal", "oats"),
      s("Peanut butter", "jar"),
      s("Jam", "jar", "jelly"),
      s("Honey", "jar"),
      s("Beans", "can", "canned beans", "black beans"),
      s("Canned tomatoes", "can", "tomato sauce", "pasta sauce"),
      s("Soup", "can"),
      s("Olive oil", "oil", "oil", "vinegar"),
      s("Coffee", "coffee"),
      s("Tea", "tea"),
      s("Salt", "salt", "spices"),
    ],
  },
  {
    id: "frozen",
    name: "Frozen",
    glyph: "ice",
    items: [
      s("Frozen veg", "frozen", "frozen vegetables", "frozen peas"),
      s("Frozen berries", "frozen"),
      s("Ice cream", "iceCream"),
      s("Frozen pizza", "pizza", "pizza"),
      s("Waffles", "waffle"),
      s("Fries", "fries"),
      s("Ice", "ice"),
    ],
  },
  {
    id: "drinks",
    name: "Drinks",
    glyph: "water",
    items: [
      s("Water", "water", "bottled water"),
      s("Sparkling water", "glass", "seltzer"),
      s("Juice", "juice", "orange juice", "apple juice"),
      s("Soda", "can", "pop", "cola"),
      s("Oat milk", "milk", "almond milk", "soy milk"),
      s("Beer", "beer"),
      s("Wine", "wine"),
    ],
  },
  {
    id: "snacks",
    name: "Snacks",
    glyph: "cookie",
    items: [
      s("Chips", "chips", "crisps"),
      s("Crackers", "cracker"),
      s("Cookies", "cookie", "biscuits"),
      s("Chocolate", "chocolate"),
      s("Popcorn", "popcorn"),
      s("Nuts", "nut", "almonds", "peanuts"),
      s("Pretzels", "pretzel"),
      s("Granola bars", "bar", "snack bars"),
    ],
  },
  {
    id: "household",
    name: "Household and cleaning",
    short: "Household",
    glyph: "spray",
    items: [
      s("Paper towels", "roll"),
      s("Toilet paper", "roll", "loo roll"),
      s("Dish soap", "soap", "dishwasher tabs"),
      s("Laundry detergent", "detergent", "detergent"),
      s("Cleaning spray", "spray", "cleaner"),
      s("Trash bags", "trashBag", "bin bags", "garbage bags"),
      s("Sponges", "sponge"),
      s("Foil", "foil", "plastic wrap", "cling film"),
      s("Batteries", "battery"),
      s("Light bulbs", "bulb"),
    ],
  },
  {
    id: "toiletries",
    name: "Toiletries",
    glyph: "toothbrush",
    items: [
      s("Toothpaste", "toothpaste"),
      s("Toothbrushes", "toothbrush"),
      s("Shampoo", "soap", "conditioner"),
      s("Soap", "soapBar", "body wash", "hand soap"),
      s("Deodorant", "deodorant"),
      s("Tissues", "tissues"),
      s("Razors", "razor"),
      s("Sunscreen", "tube", "lotion"),
    ],
  },
  {
    id: "baby",
    name: "Baby",
    glyph: "babyBottle",
    items: [
      s("Diapers", "diaper", "nappies"),
      s("Wipes", "wipes", "baby wipes"),
      s("Formula", "can"),
      s("Baby food", "jar"),
      s("Bottles", "babyBottle"),
      s("Diaper cream", "tube", "nappy cream"),
    ],
  },
  {
    id: "pet",
    name: "Pet",
    glyph: "paw",
    items: [
      s("Dog food", "bowl"),
      s("Cat food", "can"),
      s("Cat litter", "sack", "litter"),
      s("Pet treats", "bone", "dog treats", "cat treats"),
    ],
  },
];

/** A name and its likely singulars, so "Apple", "apples" and "COOKIE " find the same shortcut. */
function forms(text: string): string[] {
  const k = itemKey(text);
  return [...new Set([k, k.replace(/s$/, ""), k.replace(/es$/, ""), k.replace(/ies$/, "y"), k.replace(/ies$/, "ie")])];
}

const INDEX = new Map<string, Shortcut>();
for (const aisle of AISLES) {
  for (const item of aisle.items) {
    for (const name of [item.name, ...(item.also ?? [])]) {
      for (const k of forms(name)) if (!INDEX.has(k)) INDEX.set(k, item);
    }
  }
}

/** The shortcut a list item's text names, if any (for its icon). */
export function shortcutFor(text: string): Shortcut | undefined {
  for (const k of forms(text)) {
    const hit = INDEX.get(k);
    if (hit) return hit;
  }
  return undefined;
}
